import React, { useRef, useEffect, useState } from 'react';
import { Keypoint } from './index';
import { KeypointOverlayDiv, KeypointDot } from './styled';

interface KeypointOverlayProps {
  keypoints: Keypoint[];
}

const KeypointOverlay: React.FC<KeypointOverlayProps> = ({ keypoints }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 1, height: 1 });
  const [imgDims, setImgDims] = useState({
    width: 1,
    height: 1,
    left: 0,
    top: 0,
    imgW: 1,
    imgH: 1,
  });
  const [hovered, setHovered] = useState<null | {
    x: number;
    y: number;
    left: number;
    top: number;
  }>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const lastFrameUrl = useRef<string | undefined>(undefined);

  // Find the frame image from keypoints (they all share the same frame)
  const frameUrl = keypoints[0]?.frame?.signed_url;

  useEffect(() => {
    if (overlayRef.current) {
      setDimensions({
        width: overlayRef.current.offsetWidth,
        height: overlayRef.current.offsetHeight,
      });
    }
  }, []);

  useEffect(() => {
    // Only reload if the frameUrl actually changes
    if (frameUrl && frameUrl === lastFrameUrl.current) return;
    setImgLoaded(false);
    if (!frameUrl) return;
    lastFrameUrl.current = frameUrl;
    const img = new window.Image();
    img.onload = function () {
      if (!overlayRef.current) return;
      const containerW = overlayRef.current.offsetWidth;
      const containerH = overlayRef.current.offsetHeight;
      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      // Calculate scale and offset for object-fit: contain
      const scale = Math.min(containerW / imgW, containerH / imgH);
      const displayW = imgW * scale;
      const displayH = imgH * scale;
      const left = (containerW - displayW) / 2;
      const top = (containerH - displayH) / 2;
      setImgDims({ width: displayW, height: displayH, left, top, imgW, imgH });
      setImgLoaded(true);
    };
    img.src = frameUrl;
  }, [frameUrl, dimensions.width, dimensions.height]);

  return (
    <>
      {/* Display the frame image as the base layer */}
      {frameUrl && imgLoaded && (
        <img
          key={keypoints[0]?.frame?.uuid}
          src={frameUrl}
          alt="Frame"
          style={{
            position: 'absolute',
            left: imgDims.left,
            top: imgDims.top,
            width: imgDims.width,
            height: imgDims.height,
            objectFit: 'contain',
            zIndex: 0,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      )}
      <KeypointOverlayDiv ref={overlayRef}>
        {imgLoaded && (
          <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            {keypoints.map(kp => {
              const pairId = kp.keypoint_class?.keypoint_pair_id;
              if (pairId) {
                const pairKp = keypoints.find(k => k.keypoint_class?.uuid === pairId);
                if (pairKp) {
                  const left1 = imgDims.left + (kp.x / imgDims.imgW) * imgDims.width;
                  const top1 = imgDims.top + (kp.y / imgDims.imgH) * imgDims.height;
                  const left2 = imgDims.left + (pairKp.x / imgDims.imgW) * imgDims.width;
                  const top2 = imgDims.top + (pairKp.y / imgDims.imgH) * imgDims.height;
                  return (
                    <line
                      key={`line-${kp.id}-${pairKp.id}`}
                      x1={left1}
                      y1={top1}
                      x2={left2}
                      y2={top2}
                      stroke="#ff3b3b"
                      strokeWidth="2"
                    />
                  );
                }
              }
              return null;
            })}
          </svg>
        )}
        {imgLoaded &&
          keypoints.map(kp => {
            const left = imgDims.left + (kp.x / imgDims.imgW) * imgDims.width;
            const top = imgDims.top + (kp.y / imgDims.imgH) * imgDims.height;
            return (
              <React.Fragment key={kp.id}>
                <KeypointDot
                  style={{ left: `${left}px`, top: `${top}px` }}
                  onMouseEnter={() => setHovered({ x: kp.x, y: kp.y, left, top })}
                  onMouseLeave={() => setHovered(null)}
                />
                {hovered && hovered.x === kp.x && hovered.y === kp.y && (
                  <div
                    style={{
                      position: 'absolute',
                      left: left + 10,
                      top: top - 10,
                      background: 'rgba(0,0,0,0.85)',
                      color: '#fff',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap',
                      zIndex: 100,
                    }}
                  >
                    {kp.keypoint_class?.value
                      ? `${kp.keypoint_class.value} (${kp.x}, ${kp.y})`
                      : `(${kp.x}, ${kp.y})`}
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </KeypointOverlayDiv>
    </>
  );
};

export default KeypointOverlay;
